import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Flame, Clock } from 'lucide-react';
import { Reveal } from '../../components/Bits';
import { Img } from '../../components/Img';

const PAGE_URL = 'https://biryanify.us/blog/biryani-vs-pulav-vs-mandi';
const TITLE = 'Biryani vs. Pulav vs. Mandi: Understanding Hyderabadi Rice Dishes | Biryanify';
const DESCRIPTION =
  'What\'s the difference between biryani, pulav, and mandi? Learn the cooking methods, flavor profiles, and regional origins of these iconic Hyderabadi and Arabian rice dishes.';

const FAQS = [
  {
    q: 'What is dum biryani and why is it different from pulav?',
    a: 'Dum biryani seals the meat, rice, and spices in a handi (cooking pot) under a tight lid and slow-steams them together from start to finish. Pulav cooks rice and meat together in an open pot, stirring occasionally, so it\'s faster and less sealed. Biryani develops deeper, more complex flavors because the steam is trapped and every grain absorbs the meat\'s juices and spices.',
  },
  {
    q: 'Is mandi the same as biryani?',
    a: 'No. Mandi is an Arabian/Levantine dish where grilled meat (often marinated and spit-roasted) is served over fragrant rice cooked in the meat\'s drippings. It\'s less of a layered cook and more of an assembly—grilled protein on top of rice. Biryani is fully integrated, with meat and rice slow-cooked together from the start.',
  },
  {
    q: 'Which is spicier: biryani or pulav?',
    a: 'Spice level depends on the recipe, not the cooking method. Traditional Hyderabadi dum biryani tends to have more complex whole spices (saffron, mint, fried onion), while pulav can range from mild to spiced depending on the chef. Mandi is typically aromatic rather than fiery—more fragrant than hot.',
  },
  {
    q: 'How long does it take to cook dum biryani?',
    a: 'True dum biryani takes 45 minutes to over an hour—the meat is marinated, layered with raw rice and spices, sealed, and slow-cooked. Pulav is faster, usually 30–40 minutes. Mandi is quickest since the meat is pre-grilled; it\'s mainly about cooking the rice and assembling.',
  },
  {
    q: 'Can I make vegetarian biryani or pulav?',
    a: 'Absolutely. Both biryani and pulav work beautifully as veg dishes using paneer, potatoes, peas, and mushrooms in place of meat. The cooking method stays the same—dum for biryani, open-pot for pulav. Mandi, traditionally meat-centric, can also be adapted with grilled vegetables or paneer.',
  },
  {
    q: 'What region is biryani from, and what about pulav and mandi?',
    a: 'Dum biryani is most associated with Hyderabad, India, though versions exist across North and South India (Lucknowi, Kolkati). Pulav is popular in Andhra Pradesh and across North India. Mandi originates from Yemen and the Levantine region (Lebanon, Syria, Jordan) and is a staple in Arabian cuisine. Biryanify serves all three traditions in one kitchen.',
  },
];

export default function BiryaniVsPulavVsMandi() {
  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" />
        <meta property="og:image:alt" content="Three bowls of Hyderabadi biryani, pulav, and mandi rice dishes side by side" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://biryaniify.us/' },
                { '@type': 'ListItem', position: 2, name: 'Biryani vs. Pulav vs. Mandi', item: PAGE_URL },
              ],
            },
            {
              '@type': 'Article',
              headline: 'Biryani vs. Pulav vs. Mandi: Understanding Hyderabadi Rice Dishes',
              description: DESCRIPTION,
              author: { '@type': 'Organization', name: 'Biryanify' },
              publisher: { '@type': 'Organization', name: 'Biryanify' },
              mainEntityOfPage: PAGE_URL,
              url: PAGE_URL,
              datePublished: '2024-12-18',
              dateModified: '2024-12-18',
              image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png',
            },
            {
              '@type': 'FAQPage',
              mainEntity: FAQS.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            },
          ],
        }) }} />
      </Helmet>
      <div style={{ background: 'var(--color-surface)' }}>
        <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
          <Img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png"
            alt="Three bowls of Hyderabadi biryani, pulav, and mandi rice dishes side by side"
            eager
            className="absolute inset-0"
            imgClassName="scale-105"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.6) 0%, rgba(20,13,8,0.25) 45%, rgba(20,13,8,0.92) 100%)' }} />
          <div className="wrap relative pb-12 pt-32 md:pb-16">
            <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Food Guide</p>
            <h1 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2.2rem, 5.6vw, 3.6rem)', lineHeight: 1.05, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
              Biryani vs. Pulav vs. Mandi: Understanding Hyderabadi Rice Dishes
            </h1>
            <p className="mt-4 max-w-2xl" style={{ fontSize: '1.08rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
              All three are rice dishes rooted in centuries of tradition. Here&rsquo;s what makes each one special—and why you need to try all three.
            </p>
          </div>
        </section>

        <article className="wrap py-14 md:py-20 max-w-3xl mx-auto">
          <Reveal>
            <p className="text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.75 }}>
              If you've walked into an Indian or Arabian restaurant and spotted "biryani," "pulav," and "mandi" on the menu, you've probably wondered: what's the difference? To the untrained eye, they all look like fragrant rice cooked with meat or vegetables. But the cooking method, flavor profile, regional origin, and final taste of each dish are distinctly different.
            </p>
            <p className="mt-4 text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.75 }}>
              At <Link to="/" className="underline font-semibold">Biryanify</Link>, we specialize in all three traditions—Hyderabadi dum biryani, Andhra pulav, and Arabian mandi—each cooked the authentic way. This guide breaks down the differences so you can order with confidence and understand why each one tastes the way it does.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">What Is Dum Biryani? The Sealed, Slow-Cook Method</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Dum biryani</strong> comes from the word "dum," which means "to breathe" or "to steam" in Urdu. It's a cooking technique where:
            </p>
            <ol className="mt-5 space-y-3 text-ink-2 list-decimal list-inside" style={{ lineHeight: 1.7 }}>
              <li><strong className="text-ink">Raw basmati rice and marinated meat are layered</strong> in a handi (a round cooking pot) with whole spices, mint, fried onions, and saffron.</li>
              <li><strong className="text-ink">The pot is sealed</strong> with a tight lid (sometimes the lid is sealed with dough to trap all steam inside).</li>
              <li><strong className="text-ink">Slow-steamed over low heat</strong> for 45 minutes to over an hour. The trapped steam cooks both the meat and rice together, and every grain absorbs the meat's juices and aromatics.</li>
              <li><strong className="text-ink">No stirring.</strong> The beauty of dum is that the pot stays sealed—no peeking, no stirring. The magic happens inside.</li>
            </ol>
            <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Flavor profile:</strong> Hyderabadi dum biryani is aromatic, complex, and deeply flavorful. The slow cook allows spices to infuse every grain, and the meat becomes incredibly tender. Saffron, mint, and fried onions create a signature sweet-savory balance. <Link to="/menu" className="underline font-semibold">Try our Hyderabadi Chicken Dum Biryani or Hyderabadi Mutton Biryani</Link> to taste the difference.
            </p>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Origin:</strong> Dum biryani is most associated with Hyderabad, India, though versions exist across South Asia—Lucknowi biryani (Uttar Pradesh), Kolkata biryani (West Bengal), and even Sindhi biryani. Each region has its own spice balance and layering technique.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">What Is Pulav? The Open-Pot, Stir-and-Cook Method</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Pulav</strong> (also spelled "pilaf" or "pilau") is a rice dish where:
            </p>
            <ol className="mt-5 space-y-3 text-ink-2 list-decimal list-inside" style={{ lineHeight: 1.7 }}>
              <li><strong className="text-ink">Rice is cooked in an open pot</strong> (no tight seal) with meat, vegetables, and spices.</li>
              <li><strong className="text-ink">The cook stirs occasionally</strong> to ensure even cooking and prevent sticking.</li>
              <li><strong className="text-ink">Faster cooking time:</strong> Usually 30–40 minutes because the pot isn't sealed and heat escapes.</li>
              <li><strong className="text-ink">Less intense infusion.</strong> While the rice absorbs flavor, it doesn't develop the same depth as dum biryani because the steam and aromatics aren't fully trapped.</li>
            </ol>
            <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Flavor profile:</strong> Pulav is lighter and more delicate than biryani. It's fragrant and flavorful but less intensely spiced. The rice grains remain separate and fluffy. It's a great choice if you want the taste of Indian rice dishes without the heavier, more complex flavor of biryani.
            </p>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Origin:</strong> Pulav is especially popular in Andhra Pradesh, and versions exist across North India. <Link to="/menu" className="underline font-semibold">Biryanify's Andhra Pulavs</Link> feature veg kheema, chicken pulav, and mutton pulav—each a study in balancing texture and spice.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">What Is Mandi? The Grilled Meat & Rice Assembly</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Mandi</strong> is an Arabian/Levantine dish where:
            </p>
            <ol className="mt-5 space-y-3 text-ink-2 list-decimal list-inside" style={{ lineHeight: 1.7 }}>
              <li><strong className="text-ink">Meat is marinated and grilled</strong> (often on a vertical spit or al faham grill) until charred and smoky.</li>
              <li><strong className="text-ink">Rice is cooked separately</strong> in a pot, often enriched with the meat's drippings or a rich broth.</li>
              <li><strong className="text-ink">Assembled to order:</strong> Grilled meat is placed on top of rice (or layered together), creating a smoky, charred exterior with tender interior.</li>
              <li><strong className="text-ink">Fastest option.</strong> Since the meat is pre-grilled, mandi comes together in 15–20 minutes.</li>
            </ol>
            <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Flavor profile:</strong> Mandi is bold and smoky. The grilling adds a charred, smoky crust to the meat, and the rice is fragrant and absorbs some of that smoke. It's less about spice complexity and more about the interaction of smoke, char, and tenderness. Often served with a garlic and lemon sauce on the side.
            </p>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              <strong className="text-ink">Origin:</strong> Mandi hails from Yemen and is a staple across the Levantine region (Lebanon, Syria, Jordan, Palestine). It's become increasingly popular across the Arabian Peninsula and now in diaspora communities worldwide. <Link to="/menu" className="underline font-semibold">Biryanify's Al Faham Chicken Mandi</Link> brings this Levantine tradition to Irving.
            </p>
          </Reveal>

          <Reveal delay={140}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Dum Biryani vs. Pulav vs. Mandi: Quick Comparison</h2>
            <div className="mt-6 card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left" style={{ borderCollapse: 'collapse' }}>
                  <thead className="bg-ink text-white">
                    <tr>
                      <th className="p-3 font-semibold">Aspect</th>
                      <th className="p-3 font-semibold">Dum Biryani</th>
                      <th className="p-3 font-semibold">Pulav</th>
                      <th className="p-3 font-semibold">Mandi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-3">
                    <tr style={{ background: 'var(--color-surface)' }}>
                      <td className="p-3 font-semibold text-ink">Cooking Method</td>
                      <td className="p-3">Sealed, slow-steamed</td>
                      <td className="p-3">Open pot, stirred</td>
                      <td className="p-3">Grilled + rice assembly</td>
                    </tr>
                    <tr style={{ background: 'var(--color-card)' }}>
                      <td className="p-3 font-semibold text-ink">Cook Time</td>
                      <td className="p-3">45 min–1+ hour</td>
                      <td className="p-3">30–40 min</td>
                      <td className="p-3">15–20 min</td>
                    </tr>
                    <tr style={{ background: 'var(--color-surface)' }}>
                      <td className="p-3 font-semibold text-ink">Flavor</td>
                      <td className="p-3">Deep, complex, aromatic</td>
                      <td className="p-3">Fragrant, lighter, delicate</td>
                      <td className="p-3">Smoky, charred, bold</td>
                    </tr>
                    <tr style={{ background: 'var(--color-card)' }}>
                      <td className="p-3 font-semibold text-ink">Spice Profile</td>
                      <td className="p-3">Whole spices, saffron, mint</td>
                      <td className="p-3">Mild to moderate</td>
                      <td className="p-3">Aromatic, not primarily spiced</td>
                    </tr>
                    <tr style={{ background: 'var(--color-surface)' }}>
                      <td className="p-3 font-semibold text-ink">Origin</td>
                      <td className="p-3">Hyderabad, South Asia</td>
                      <td className="p-3">Andhra Pradesh, North India</td>
                      <td className="p-3">Yemen, Levantine region</td>
                    </tr>
                    <tr style={{ background: 'var(--color-card)' }}>
                      <td className="p-3 font-semibold text-ink">Best For</td>
                      <td className="p-3">Celebration, special occasions</td>
                      <td className="p-3">Weeknight dinner</td>
                      <td className="p-3">Quick, flavorful lunch</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Why You Should Try All Three at Biryanify</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              Biryanify is one of the few Indian restaurants in Irving that offers authentic versions of all three traditions. Here's why each is worth ordering:
            </p>
            <div className="mt-6 space-y-4">
              <div className="card p-6">
                <h3 className="font-display text-lg text-ink flex items-start gap-3">
                  <Flame size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                  Dum Biryani for Complex, Layered Flavor
                </h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  If you want the "wow" moment—the deep, aromatic, complexity that keeps you coming back—order biryani. It takes longer and requires more technique, which is exactly why it tastes so good.
                </p>
              </div>
              <div className="card p-6">
                <h3 className="font-display text-lg text-ink flex items-start gap-3">
                  <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                  Pulav for Lighter, Faster Satisfaction
                </h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  If you want authentic Indian rice without the heavier spice load or the wait time, pulav is your answer. It's elegant and flavorful without being overwhelming.
                </p>
              </div>
              <div className="card p-6">
                <h3 className="font-display text-lg text-ink flex items-start gap-3">
                  <Flame size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                  Mandi for Smoke-Grilled Drama
                </h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  If you love smoky, charred, grilled flavors, mandi hits a completely different note. It's Arabian, it's bold, and it's a welcome break from the Indian spice palette.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Can You Make Them Vegetarian?</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              Yes! All three dishes work beautifully as vegetarian options. Biryani can feature paneer, potato, mushroom, or chickpea. Pulav adapts easily with mixed vegetables or paneer. And while mandi is traditionally meat-centric, grilled paneer or seasonal vegetables can create a hearty vegetarian version. <Link to="/menu" className="underline font-semibold">Explore Biryanify's vegetarian biryani, pulav, and rice bowls</Link> to see what's available.
            </p>
          </Reveal>

          <Reveal delay={200}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Frequently Asked Questions</h2>
            <div className="mt-6 space-y-6">
              {FAQS.map((f) => (
                <div key={f.q} className="card p-6">
                  <h3 className="font-display text-lg text-ink">{f.q}</h3>
                  <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-14 card p-8 text-center" style={{ background: 'var(--color-surface-2)' }}>
              <h2 className="font-display text-2xl text-ink">Ready to taste all three?</h2>
              <p className="mt-3 text-ink-2">Browse the menu and order your Hyderabadi biryani, Andhra pulav, or Arabian mandi today.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/menu" className="btn btn-primary">View the menu <ArrowRight size={16} /></Link>
                <Link to="/visit" className="btn btn-ghost">Get directions</Link>
              </div>
            </div>
          </Reveal>
        </article>
      </div>
    </>
  );
}
